import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';

import { ApThemeService } from '@ap/theme';

@Injectable({
    providedIn: 'root'
})
export class ApInlineSvgCacheService {
    private readonly themeService = inject(ApThemeService);
    private rendererFactory = inject(RendererFactory2);

    private static cache: Map<string, SVGElement>;
    private static inProgressRequests: Map<string, Promise<string>>;
    private baseUrl: string;

    private readonly renderer: Renderer2;

    constructor() {
        this.renderer = this.rendererFactory.createRenderer(null, null);
        this.baseUrl = this.themeService.getStaticUrl();

        if (!ApInlineSvgCacheService.cache) {
            ApInlineSvgCacheService.cache = new Map<string, SVGElement>();
        }
        if (!ApInlineSvgCacheService.inProgressRequests) {
            ApInlineSvgCacheService.inProgressRequests = new Map<string, Promise<string>>();
        }
    }

    /**
     * Get SVG element from cache or HTMl request
     */
    public async getSVG(url: string, resolveSVGUrl: boolean, cache = true): Promise<SVGElement> {
        const svgUrl = (resolveSVGUrl ? this.getAbsoluteUrl(url) : url).replace(/#.+$/, '');

        // Return cached copy of already loaded SVG if it exists
        if (cache && ApInlineSvgCacheService.cache.has(svgUrl)) {
            return Promise.resolve(this.cloneSVG(ApInlineSvgCacheService.cache.get(svgUrl)));
        }

        try {
            const svgText = await this.createSvgRequest(svgUrl);
            const svgEl = this.svgElementFromString(svgText);
            ApInlineSvgCacheService.cache.set(svgUrl, svgEl);
            return this.cloneSVG(svgEl);
        } catch (e) {
            ApInlineSvgCacheService.cache.set(svgUrl, null);
            throw new Error(e);
        } finally {
            ApInlineSvgCacheService.inProgressRequests.delete(svgUrl);
        }
    }

    /**
     *
     * @param svgUrl
     * @returns
     */
    private async createSvgRequest(svgUrl: string): Promise<string> {
        // Return existing fetch Promise
        if (ApInlineSvgCacheService.inProgressRequests.has(svgUrl)) {
            return ApInlineSvgCacheService.inProgressRequests.get(svgUrl);
        }

        // Otherwise, make the HTTP call to fetch
        const request = fetch(svgUrl, { method: 'GET' })
            .then((response) => response.text())
            .catch((e) => {
                ApInlineSvgCacheService.cache.set(svgUrl, null);
                throw new Error(e);
            })
            .finally(() => {
                ApInlineSvgCacheService.inProgressRequests.delete(svgUrl);
            });
        ApInlineSvgCacheService.inProgressRequests.set(svgUrl, request);
        return request;
    }

    getAbsoluteUrl(url: string): string {
        let absoluteUrl = url;
        // Prepend user-configured base if present and URL doesn't seem to have its own
        if (this.baseUrl && !/^https?:\/\//i.test(url)) {
            absoluteUrl = this.baseUrl + url;

            // Convert leading "//" to "/" to prevent a malformed URL
            // See https://github.com/arkon/ng-inline-svg/issues/50
            if (absoluteUrl.indexOf('//') === 0) {
                absoluteUrl = absoluteUrl.substring(1);
            }
        }

        const base = this.renderer.createElement('BASE');
        base.href = absoluteUrl;

        return base.href;
    }

    private svgElementFromString(str: string): SVGElement | never {
        if (str.indexOf('data:image/svg+xml;base64') === 0) {
            const img = this.renderer.createElement('img');
            img.src = str;
            return img as SVGElement;
        }

        const div = this.renderer.createElement('DIV');
        div.innerHTML = str;

        const svg = div.querySelector('svg') as SVGElement;
        if (!svg) {
            throw new Error('No SVG found in loaded contents');
        }

        return svg;
    }

    private cloneSVG(svg: SVGElement): SVGElement {
        return svg.cloneNode(true) as SVGElement;
    }
}
